import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { catchError, of } from 'rxjs';
import { SpaceRepository } from '../state/space.repository';
import { Frame, Session } from '../models/session';
import { AuthService } from './auth.service';
import { UserService } from './user.service';
import { URLService } from './dynamic-url.service';
import { SpaceBoardsService } from './space-boards.service';
import { RealtimeService } from './realtime.service';

/**
 * Server-side event types — mirror plugin/src/streams/event_bus.ts.
 */
export interface AgentStreamTextEvent {
  kind: 'text';
  sessionKey: string;
  text: string;
  ts: number;
  /**
   * Display name of the user behind this turn — set by the orchestrator from the
   * <<<SB_CLIENT>>> attribution marker (shared/collaborative spaces). Shown as a
   * byline on user turns in the panel.
   */
  user?: string;
  /**
   * Mongo id of the user behind this turn (same SB_CLIENT marker as `user`).
   * Lets the panel resolve the author against the space roster and show their
   * real avatar; matching on display name alone would hand two people with the
   * same name each other's face, in a feed the whole space reads.
   *
   * Often absent — voice, email and system turns carry no uid — so every
   * consumer must degrade to the name and then to initials.
   */
  userUid?: string;
  /**
   * Platform-queue id this turn was dispatched under — set by the orchestrator
   * from the same <<<SB_CLIENT>>> marker, on `[user] ` echoes only.
   *
   * This is the handoff key: seeing it means history now draws the message, so
   * the optimistic row shown since the user pressed send can be retired. Matching
   * on it rather than on the text is what keeps the handoff working when
   * pencil-api appends anything to the outgoing message at dispatch (attachment
   * block, locale directive). Absent for turns that never went through the queue,
   * and absent from orchestrators older than this field — both fall back to the
   * legacy text match.
   */
  queueMessageId?: string;
  /** Set on a user turn that came in from Slack: who really wrote it. pencil-api
   *  attaches it; see {@link MessageVia}. */
  via?: MessageVia;
}

/**
 * Who a machine-delivered message is really from (pencil-api `messageVia`). For a
 * Slack message the panel shows `name` "via Slack, #channel" as the byline and
 * never draws the turn as the account it ran under: for someone outside the space
 * that account is the member who opened the thread, not the person who wrote it.
 */
export interface MessageVia {
  kind: 'slack';
  name: string;
  channel?: string;
  member: boolean;
}

export interface AgentStreamToolCallEvent {
  kind: 'tool_call';
  sessionKey: string;
  toolUseId: string;
  toolName: string;
  argsPreview: string;
  ts: number;
}

/**
 * A board item a tool just created, so the panel can offer "show me" and jump
 * the local viewport to it (JUMP_TO_ITEM_SPEC.md). Mirrors
 * plugin/src/streams/board_ref.ts.
 *
 * `frameUid` is the board the item was PLACED on. If someone later drags the
 * item to a different board this goes stale — resolution fails and the jump
 * reports that rather than moving the user somewhere wrong.
 */
export interface BoardRef {
  itemUid: string;
  frameUid: string;
  kind:
    | 'doc'
    | 'sheet'
    | 'deck'
    | 'viewer'
    | 'app'
    | 'pdf'
    | 'image'
    | 'shapes'
    | 'item';
  title?: string;
  /** Batch placements (a drawing): every uid, so the jump can frame the WHOLE
   *  thing instead of one corner of it. `itemUid` is the first, as a fallback. */
  itemUids?: string[];
}

export interface AgentStreamToolResultEvent {
  kind: 'tool_result';
  sessionKey: string;
  toolUseId: string;
  toolName?: string;
  resultPreview: string;
  isError: boolean;
  /** Set only by tools that PLACE something on the board. */
  boardRef?: BoardRef;
  ts: number;
}

export interface AgentStreamSpeakEvent {
  kind: 'speak';
  sessionKey: string;
  text: string;
  ts: number;
}

export interface AgentStreamTurnEvent {
  kind: 'turn_started' | 'turn_completed';
  sessionKey: string;
  stopReason?: string;
  ts: number;
}

export interface AgentStreamSubAgentSpawnEvent {
  kind: 'subagent_spawn';
  sessionKey: string; // parent sessionKey (where the spawn happened)
  parentSessionKey: string;
  childSessionKey: string;
  label: string;
  task: string;
  ts: number;
}

export interface AgentStreamSubAgentDoneEvent {
  kind: 'subagent_done';
  sessionKey: string; // parent sessionKey
  parentSessionKey: string;
  childSessionKey: string;
  label?: string;
  status: 'success' | 'failed';
  resultPreview: string;
  ts: number;
}

/** One milestone in the pinned task-checklist (mirrors plugin event_bus.ts). */
export interface AgentStreamPlanItem {
  id: string;
  label?: string;
  status: 'pending' | 'active' | 'done' | 'failed';
  substatus?: string;
}

export interface AgentStreamPlanEvent {
  kind: 'plan';
  sessionKey: string;
  items: AgentStreamPlanItem[];
  title?: string;
  /** When true, clears the existing checklist before applying these items. */
  reset?: boolean;
  ts: number;
}

/**
 * Canonical, merged checklist state the panel header renders. The service owns
 * this (merge-by-id) so the agent can send only the items that changed.
 */
export interface PlanState {
  title?: string;
  items: AgentStreamPlanItem[];
}

export type AgentStreamEvent =
  | AgentStreamTextEvent
  | AgentStreamToolCallEvent
  | AgentStreamToolResultEvent
  | AgentStreamSpeakEvent
  | AgentStreamTurnEvent
  | AgentStreamSubAgentSpawnEvent
  | AgentStreamSubAgentDoneEvent
  | AgentStreamPlanEvent;

/**
 * Stream connection state, surfaced so the panel can show a status chip.
 * `connecting` doubles as "starting the agent…" — during the window where the
 * orchestrator isn't ready yet, the stream is legitimately in the connect/retry
 * loop with no frames, which reads to the user as "still coming up".
 */
export type ConnectionState =
  | 'idle' // panel closed / not connected
  | 'connecting' // first connect (or the agent is still spinning up)
  | 'live' // stream open + delivering
  | 'reconnecting'; // was live, dropped, retrying with backoff

/**
 * A user message the FE shows OPTIMISTICALLY the instant it's sent, before the
 * orchestrator echoes it back (INTERACTIVE_QUEUE_SPEC.md, Phase 1). Kept separate
 * from `history` (which is cleared+rebuilt from the replay on every reconnect) so
 * optimistic turns survive reconnects. Reconciled away when the server echoes the
 * turn (a `text` event with `user` set). `clientId` is plumbed through /utter now
 * (inert on the backend until Phase 2) so reconcile can move from content-match to
 * exact id-match with no FE change.
 */
/**
 * Split the hosted-attachment block off a user message.
 *
 * pencil-api appends the block to `initialMessage` at dispatch, and the
 * orchestrator echoes that message back verbatim — so the raw storage URLs
 * arrive in the transcript whether or not we ever stored them. Rendering that
 * as-is puts a signed link in front of everyone in the space, under someone
 * else's message.
 *
 * So the panel splits it: the prose renders, the filenames become chips, and
 * the URLs are dropped on the floor. Parsing the echo (rather than remembering
 * what we sent) also means the chips survive a reload — the transcript is the
 * only thing that does.
 */
export function splitAttachments(text: string): { body: string; files: string[] } {
  const marker = text.indexOf('Attachments (already hosted');
  if (marker < 0) {
    return { body: text, files: [] };
  }
  const files: string[] = [];
  for (const line of text.slice(marker).split('\n')) {
    // "- name.pdf (application/pdf, 12 KB) -> https://..."
    // GREEDY name, so a filename that itself contains parentheses still parses.
    // "resume (1).pdf" is not exotic -- it is what every browser produces on a
    // second download -- and a lazy match would stop at "resume", fail to find
    // the arrow, and silently drop the chip.
    const m = /^-\s+(.*)\s+\([^()]*\)\s*(?:→|->)\s*\S+$/.exec(line.trim());
    if (m) {
      files.push(m[1]);
    }
  }
  return { body: text.slice(0, marker).trimEnd(), files };
}

/** A file already uploaded to storage, travelling with an outgoing message. */
export interface OutgoingAttachment {
  filename: string;
  mimeType: string;
  url: string;
  size?: number;
}

/** pencil-api's refusal when the sender may not message this space's agent. */
const AGENT_MESSAGES_MEMBERS_ONLY = 'agent_messages_members_only';
const AGENT_MESSAGES_MEMBERS_ONLY_FALLBACK =
  "Only members of this space's organization can message the agent here. " +
  'Ask the owner or a host of this space to give you permission to message the agent.';

export interface PendingUserTurn {
  clientId: string;
  text: string;
  ts: number;
  /** Who sent it (self, for the byline). */
  author?: string;
  /** Mongo id of the sender, when known — server-queue rows carry it, and it is
   *  what lets the panel show that person's real avatar rather than guessing
   *  from a display name. Absent on purely optimistic local rows until the
   *  server confirms them. */
  authorUid?: string;
  status: 'sending' | 'sent' | 'queued' | 'active' | 'failed';
  /**
   * Server-minted queue id (PLATFORM_QUEUE_SPEC.md). Present once pencil-api has
   * accepted the message. THE reconciliation key — its arrival is also what flips
   * this turn from a local guess to server-confirmed state.
   */
  msgId?: string;
  /** True when this row came from the server queue rather than local optimism,
   *  i.e. it may belong to ANOTHER user in the space. */
  fromServer?: boolean;
  /** Files sent with this message. Carried so the feed can show WHAT was
   *  attached: the URLs are no longer inside `text` (they would render as a raw
   *  link dump for the whole room), so without this the message reads as if the
   *  user asked about documents they never sent. */
  attachments?: OutgoingAttachment[];
  /**
   * Whether THIS viewer may edit or cancel this row — author, or host
   * (PLATFORM_QUEUE_SPEC.md §12b). Computed here rather than sent per-message,
   * because one queue payload is broadcast to every panel in the space and so
   * cannot carry a per-viewer answer.
   *
   * Purely an affordance hint. pencil-api re-checks authorship inside the Mongo
   * arrayFilter, so a client that ignores this simply gets a 409.
   */
  canMutate?: boolean;
  /** Why the server refused to take this message, worded for the sender, when it
   *  said (e.g. only members of the space's organization may message the agent). */
  failReason?: string;
  /** Set when the message came in from Slack. */
  via?: MessageVia;
}

/** One message as pencil-api reports it on the queue wire. */
/**
 * The stored-transcript wire shape (pencil-api GET /ai-session/:id/messages).
 * Read only behind `?transcriptSource=db`; see `loadStoredHistory`.
 */
interface StoredTranscriptRow {
  kind: string;
  role?: string;
  text?: string;
  ts?: string;
  seq?: number;
  epoch?: number;
  sessionKey?: string;
  isSubagent?: boolean;
  toolName?: string;
  toolUseId?: string;
  argsPreview?: string;
  resultPreview?: string;
  isError?: boolean;
  stopReason?: string;
  queueMessageId?: string;
  /** Who sent a user row: display name and Mongo user id (stored from the live
   *  echo's `user` / `userUid`). */
  authorName?: string;
  authorUid?: string;
  via?: MessageVia;
}

interface StoredTranscriptTurn {
  turnId: string;
  isGap: boolean;
  steps?: StoredTranscriptRow[];
}

interface StoredTranscriptPage {
  turns: StoredTranscriptTurn[];
  cursor: { before: string | null; hasMore: boolean };
  /** Newest generation, and its highest stored seq — together the watermark. */
  epoch: number | null;
  latestSeq: number | null;
}

interface QueueWireMessage {
  msgId: string;
  text: string;
  /** What to SHOW, when it differs from what the agent was sent. Set only for
   *  machine-assembled turns (an inbound email arrives wrapped in routing
   *  metadata and instructions written at the agent); absent for anything a
   *  person typed, hence the fallback to `text` at every use. */
  displayText?: string;
  status: 'queued' | 'dispatching' | 'active' | 'done' | 'failed' | 'cancelled';
  clientId?: string;
  /** Mongo user id of whoever queued it. Firebase mints its uid AS the Mongo
   *  `_id`, so this compares directly against the signed-in user's `_id`. */
  authorUid?: string;
  authorName?: string;
  createdAt?: string;
  via?: MessageVia;
}

/** Reply to a cancel/edit. `state` rides on BOTH the 200 and the 409, because
 *  the useful thing about "you can't" is being told what is actually true. */
interface QueueMutationReply {
  ok?: boolean;
  err?: string;
  state?: QueueWireState;
}

/** The queue snapshot / delta payload. Every Galactus queue event carries the
 *  WHOLE state, not just the delta, so any event is also a full resync. */
export interface QueueWireState {
  spaceId: string;
  state: 'idle' | 'dispatching' | 'busy' | 'stuck';
  activeMessageId?: string;
  messages: QueueWireMessage[];
}

/**
 * Live stream of orchestrator + sub-agent activity for the active space.
 *
 * Why fetch+ReadableStream instead of EventSource:
 *   pencil-api auths via `Authorization: Bearer <firebase-id-token>` headers.
 *   EventSource doesn't support custom headers — only cookies via
 *   withCredentials, and pencil-api's CORS responds `Access-Control-Allow-
 *   Origin: *` which the browser refuses to pair with credentialed requests.
 *
 *   fetch() with response.body.getReader() lets us set Authorization, stream
 *   the response chunks, and parse SSE format manually (it's just `data: <json>
 *   \n\n` framing). We re-implement EventSource's auto-reconnect ourselves.
 */
@Injectable({ providedIn: 'root' })
export class AiSessionStreamService {
  private readonly panelOpenSubject = new BehaviorSubject<boolean>(false);
  private readonly eventsSubject = new Subject<AgentStreamEvent>();
  /** Full ordered history. Held in memory only — never persisted. */
  private readonly historySubject = new BehaviorSubject<AgentStreamEvent[]>([]);
  /** Merged task-checklist state (merge-by-id) for the pinned panel header. */
  private readonly planSubject = new BehaviorSubject<PlanState>({ items: [] });

  /** Connection status for the panel's chip (INTERACTIVE_QUEUE_SPEC.md item 1). */
  private readonly connectionStateSubject = new BehaviorSubject<ConnectionState>('idle');
  /** Optimistic user turns, shown instantly; reconciled against the server echo. */
  private readonly pendingUserTurnsSubject = new BehaviorSubject<PendingUserTurn[]>([]);

  readonly panelOpen$: Observable<boolean> = this.panelOpenSubject.asObservable();
  readonly events$: Observable<AgentStreamEvent> = this.eventsSubject.asObservable();
  readonly history$: Observable<AgentStreamEvent[]> = this.historySubject.asObservable();
  readonly plan$: Observable<PlanState> = this.planSubject.asObservable();
  readonly connectionState$: Observable<ConnectionState> =
    this.connectionStateSubject.asObservable();
  readonly pendingUserTurns$: Observable<PendingUserTurn[]> =
    this.pendingUserTurnsSubject.asObservable();

  /**
   * Whether the agent is currently mid-turn. Phase-1 INFERENCE (deliberately dumb)
   * from turn_started/turn_completed, used only to decide whether a freshly-sent
   * message is `queued` (agent busy) vs `active`. Phase 2 replaces this with
   * authoritative queue_* events — see deriveQueueState in INTERACTIVE_QUEUE_SPEC.md.
   */
  private turnActive = false;

  /**
   * True once pencil-api has told us about the platform queue for this space
   * (PLATFORM_QUEUE_SPEC.md). While false we keep the old Phase-1 behaviour —
   * local optimism plus content-matched reconcile — so the panel still works
   * against a pencil-api that predates the queue. Once true the SERVER owns the
   * list, and the local inference is switched off rather than left to fight it.
   */
  private queueAuthoritative = false;

  /**
   * Texts of running messages the orchestrator has already echoed onto the SSE
   * stream, i.e. that `history` is now drawing. Keyed by text because the echo
   * carries no message id (`<<<SB_CLIENT>>>` has uid/name, not msgId), so there
   * is nothing else to match on.
   *
   * Only ever consulted for RUNNING rows, never queued ones, so an identically
   * worded message from someone else still queued behind this one cannot be
   * hidden by it. Cleared with `history` — the two are a matched pair, and an
   * entry that outlived the history it refers to would suppress a later
   * identical message that has no bubble to hand off to.
   */
  private readonly echoedRunningTexts = new Set<string>();

  /** Keys of feed events already rendered, so a reconnect's replay of the whole
   *  buffer is a no-op instead of a second copy of the session. Lifetime is
   *  exactly `history`'s — cleared everywhere history is. See `eventKey`. */
  private readonly seenEventKeys = new Set<string>();

  /** Queue ids the orchestrator has echoed, i.e. that `history` now draws. The
   *  id-keyed counterpart of `echoedRunningTexts`, and paired with history for
   *  the same reason: an id outliving the history it refers to would suppress a
   *  later message that has no bubble to hand off to. */
  private readonly echoedQueueMsgIds = new Set<string>();

  /** Last queue state the server reported. Rendered as the pending list. */
  private serverQueue: QueueWireState | null = null;
  /** Slack origins of queue messages seen this session, so a live echo (which
   *  carries only the queue id) can be bylined like the stored transcript. */
  private readonly viaByMsgId = new Map<string, MessageVia>();

  /** Who really wrote a queued message that came in from Slack, if it did. */
  viaFor(msgId?: string): MessageVia | undefined {
    return msgId ? this.viaByMsgId.get(msgId) : undefined;
  }

  /** Synchronous read of the current panel open/closed state. */
  get isPanelOpen(): boolean {
    return this.panelOpenSubject.value;
  }

  private abortController?: AbortController;
  private currentSpaceId?: string;
  private reconnectAttempt = 0;
  /**
   * The space the current optimistic `pendingUserTurns` belong to. Tracked
   * separately from `currentSpaceId` (which `disconnect()` nulls) so a space
   * switch — even across a panel close/reopen — can drop stale pending turns
   * instead of leaking them into another space's panel. Set when a pending turn
   * is added; cleared when the pending set is dropped on a space change.
   */
  private pendingSpaceId?: string;

  constructor(
    private http: HttpClient,
    private spaceRepo: SpaceRepository,
    private authService: AuthService,
    private userService: UserService,
    private zone: NgZone,
    private urlService: URLService,
    private spaceBoardsService: SpaceBoardsService,
    private realtimeService: RealtimeService,
  ) {
    this.panelOpenSubject.subscribe((open) => {
      if (open) {
        this.connect();
        // Tell the orchestrator which board the user is on the moment the AI
        // session engages, so the agent's first turn already targets it.
        this.pushActiveFrame();
      } else {
        this.disconnect();
      }
    });
    // Keep the orchestrator's notion of the active board fresh as the user
    // switches board tabs (so voice turns — which don't carry it — land on the
    // right board). Multi-board awareness; see MULTI_BOARD_AWARENESS_SPEC.md.
    this.spaceBoardsService.activeSpaceSelectedBoard$.subscribe((board) => {
      if (this.panelOpenSubject.value) {
        this.pushActiveFrame(board);
      }
    });
    // If the panel is opened before SpaceRepository has settled on an
    // activeSpaceId, our initial connect() silently no-ops. Re-trigger
    // when the activeSpaceId changes so the SSE eventually opens.
    this.spaceRepo.activeSpaceId$.subscribe(() => {
      if (this.panelOpenSubject.value) {
        this.connect();
      }
    });
    // Queue events ride the GALACTUS socket, not the SSE stream, so the two fail
    // independently: the socket can drop and come back while the SSE stream stays
    // healthy the whole time. `scheduleReconnect()` only covers the SSE side, so
    // that window used to be an unrecoverable hole — Galactus has no replay
    // buffer, so every queue transition in the gap was lost for good and the
    // panel kept rendering a queue that had stopped being true (a message shown
    // as queued that the agent had already answered, or someone else's message
    // never appearing at all). Re-fetch the authority on every (re)connection.
    // No-ops while the panel is closed: `fetchQueueSnapshot` returns early
    // without a `currentSpaceId`, which is also why the immediate emission this
    // pipe makes on subscribe costs nothing here.
    this.realtimeService.realtimeInitOrReconnect$.subscribe(() => {
      void this.fetchQueueSnapshot();
    });
  }

  togglePanel(): void {
    this.panelOpenSubject.next(!this.panelOpenSubject.value);
  }

  setPanelOpen(open: boolean): void {
    this.panelOpenSubject.next(open);
  }

  clearHistory(): void {
    this.historySubject.next([]);
    // Paired with history: these only exist to say "history already draws this".
    this.echoedRunningTexts.clear();
    this.echoedQueueMsgIds.clear();
    this.seenEventKeys.clear();
    this.planSubject.next({ items: [] });
  }

  /**
   * Identity of a feed event, for suppressing the replay a reconnect re-delivers.
   *
   * The old code assumed this was impossible — "feed events carry no universal
   * id, so we can't dedup a full replay". Measured on the wire, every event
   * carries a millisecond `ts` AND `kind` AND `sessionKey`, and each kind adds a
   * discriminator: `turn_completed` an `id`, tool events a `toolUseId`, `text`
   * its own body. `ts` is stamped by the orchestrator, so a replayed event is
   * byte-identical to the one already rendered while two genuinely distinct
   * events would have to share a millisecond AND a body to collide.
   *
   * Returns null for anything unrecognised, which is then never deduped — an
   * event we can't key is rendered, exactly as before. Duplicating is a cosmetic
   * bug; dropping a real event is a lost message.
   */
  private eventKey(ev: AgentStreamEvent): string | null {
    const e = ev as AgentStreamEvent & {
      ts?: number;
      id?: string;
      toolUseId?: string;
      text?: string;
    };
    if (typeof e.ts !== 'number' || !e.kind) {
      return null;
    }
    // Slice the body: a `text` event's payload can be very large (the locale
    // directive alone is ~150 words), and we only need it to disambiguate two
    // events sharing a millisecond.
    const discriminator =
      e.id ?? e.toolUseId ?? (typeof e.text === 'string' ? e.text.slice(0, 120) : '');
    return `${e.kind}|${e.sessionKey ?? ''}|${e.ts}|${discriminator}`;
  }

  /**
   * Merge a `plan` event into the canonical checklist (upsert by id):
   * `reset` clears first; an unseen id is appended in arrival order; a known id
   * updates its label/status/substatus in place.
   */
  private applyPlanEvent(ev: AgentStreamPlanEvent): void {
    const current = this.planSubject.value;
    // Auto-reset: if the previous checklist fully completed, a new plan event
    // starts a fresh task — clear first so old "done" items can't leak in (the
    // agent reuses ids like s1/s2 across tasks). Mid-task stacking still appends
    // because pending/active items remain. Explicit reset always clears.
    const priorComplete =
      current.items.length > 0 && current.items.every((i) => i.status === 'done');
    const prev = ev.reset || priorComplete ? { items: [] as AgentStreamPlanItem[] } : current;
    const items = prev.items.slice();
    for (const incoming of ev.items ?? []) {
      if (!incoming || !incoming.id) {
        continue;
      }
      const idx = items.findIndex((it) => it.id === incoming.id);
      if (idx === -1) {
        // A brand-new id with no label has nothing to render — appending it
        // would draw a blank row with a status dot. This happens when a plan
        // event carries a status-only update for an id we never saw (e.g. the
        // first `plan` after a reset/flush, or a mid-stream reconnect that
        // missed the labelled event). Drop it; the labelled event upserts it.
        if (!incoming.label) {
          continue;
        }
        items.push({ ...incoming });
      } else {
        items[idx] = {
          ...items[idx],
          status: incoming.status,
          ...(incoming.label !== undefined ? { label: incoming.label } : {}),
          ...(incoming.substatus !== undefined ? { substatus: incoming.substatus } : {}),
        };
      }
    }
    this.planSubject.next({
      title: ev.title !== undefined ? ev.title : prev.title,
      items,
    });
  }

  /**
   * Send a text message to the orchestrator (the chat-input path —
   * non-voice). Fire-and-forget: the SSE stream will surface the user
   * message + the orchestrator's response as they happen. We don't block
   * on the round-trip.
   *
   * Returns a Promise that resolves when the request has been accepted by
   * pencil-api (not when the orchestrator has responded). The caller can
   * use that to flip a "sending…" indicator off the input.
   */
  async sendMessage(
    text: string,
    attachments?: OutgoingAttachment[],
  ): Promise<boolean> {
    const trimmed = text.trim();
    if (!trimmed) {
      return false;
    }
    const spaceId = this.spaceRepo.activeSpaceId;
    if (!spaceId) {
      return false;
    }
    // Optimistic echo: show the message INSTANTLY, before the round-trip
    // (INTERACTIVE_QUEUE_SPEC.md item 2). Reconciled away when the orchestrator
    // echoes the turn back on the stream.
    const clientId = this.newClientId();
    // Tag this optimistic turn's space so a later space switch can drop it if it
    // was never reconciled (see connect()).
    this.pendingSpaceId = spaceId;
    this.addPending({
      clientId,
      text: trimmed,
      ts: Date.now(),
      status: 'sending',
      ...(attachments?.length ? { attachments } : {}),
    });

    const url = `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/utter`;
    // Carry the active board inline (text path) so the agent targets the board
    // the user is looking at (MULTI_BOARD_AWARENESS_SPEC.md). `clientId` is the
    // reconcile key now and the queue-entry id in Phase 2 (backend ignores it
    // until then — INTERACTIVE_QUEUE_SPEC.md §4a).
    const body: Record<string, unknown> = { text: trimmed, clientId };
    // Files the user attached, already uploaded to Firebase by the composer —
    // URLs only, never bytes (PANEL_FILE_UPLOAD_SPEC.md). pencil-api re-validates
    // and renders them for the agent at dispatch.
    if (attachments?.length) {
      body.attachments = attachments;
    }
    const activeFrame = this.activeFramePayload();
    if (activeFrame) {
      body.activeFrame = activeFrame;
    }
    let failReason: string | undefined;
    const result = await firstValueFrom(
      this.http.post<{ msgId?: string; dispatched?: boolean }>(url, body).pipe(
        catchError((err: HttpErrorResponse) => {
          // Someone from outside the space's organization, in a space whose hosts
          // haven't let outsiders message the agent: the server words the reason.
          if (err?.error?.err === AGENT_MESSAGES_MEMBERS_ONLY) {
            failReason = err.error.message || AGENT_MESSAGES_MEMBERS_ONLY_FALLBACK;
          }
          return of(null);
        }),
      ),
      // The app's error interceptor answers most errors by completing empty.
      { defaultValue: null },
    );
    if (result === null) {
      this.updatePending(clientId, {
        status: 'failed',
        ...(failReason ? { failReason } : {}),
      });
      return false;
    }
    // The platform queue answers authoritatively: `dispatched` says whether this
    // message is running NOW or parked behind one that is, and `msgId` is the id
    // every later Galactus event and the orchestrator's echo key on. When the
    // backend predates the queue neither field is present — fall back to the old
    // inference from turn_started/turn_completed.
    if (result.msgId) {
      this.updatePending(clientId, {
        msgId: result.msgId,
        status: result.dispatched ? 'active' : 'queued',
        // We just sent it, so it is ours by construction. Set here rather than
        // waiting for the queue broadcast to say so, or the row would render
        // without its own author's edit/cancel buttons for a beat.
        canMutate: true,
      });
    } else {
      this.updatePending(clientId, { status: this.turnActive ? 'queued' : 'active' });
    }
    return true;
  }

  /**
   * Halt ALL orchestrator work for the active space (STOP_INTERRUPT_SPEC.md).
   *
   * POSTs to /ai-session/:id/stop, which cancels the in-flight turn AND kills
   * every spawned sub-agent; the panel's "N helpers running" clears when the
   * resulting `subagent_done` + `turn_completed(stopped)` events arrive on the
   * live SSE stream. Fire-and-forget beyond the accept: resolves true once
   * pencil-api accepts the request. History is preserved (stop ≠ reset).
   */
  async stopSession(): Promise<boolean> {
    const spaceId = this.spaceRepo.activeSpaceId;
    if (!spaceId) {
      return false;
    }
    // Stop drops ALL queued + in-flight work server-side (OpenClaw deleteSession).
    // Today the FE only mirrors OpenClaw's opaque queue via optimistic turns that
    // are reconciled away by the server ECHO — but dropped queued messages never
    // run and so never echo, which would leave their "Queued" placeholders stuck
    // in the panel forever. So clear the optimistic queue on Stop. Confirmed turns
    // already in history are untouched (they render from history, not pending); the
    // stopped turn settles via turn_completed(stopped). (Small fix; the real fix is
    // the adapter-owned queue in INTERACTIVE_QUEUE_SPEC.md.)
    this.zone.run(() => this.pendingUserTurnsSubject.next([]));
    this.pendingSpaceId = undefined;
    const url = `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/stop`;
    const result = await firstValueFrom(this.http.post(url, {}).pipe(catchError(() => of(null))));
    return result !== null;
  }

  // ── Optimistic user turns (INTERACTIVE_QUEUE_SPEC.md Phase 1) ───────────────
  private newClientId(): string {
    try {
      return crypto.randomUUID();
    } catch {
      return `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
  }

  private addPending(turn: PendingUserTurn): void {
    this.zone.run(() =>
      this.pendingUserTurnsSubject.next([...this.pendingUserTurnsSubject.value, turn]),
    );
  }

  private updatePending(clientId: string, patch: Partial<PendingUserTurn>): void {
    this.zone.run(() =>
      this.pendingUserTurnsSubject.next(
        this.pendingUserTurnsSubject.value.map((t) =>
          t.clientId === clientId ? { ...t, ...patch } : t,
        ),
      ),
    );
  }

  /**
   * The orchestrator echoed a user turn (a `text` event with `user` set) → drop
   * the matching optimistic turn; the confirmed one now renders in history. Phase 1
   * matches by content (no id echoed yet); Phase 2 matches the round-tripped
   * clientId exactly. Removes at most one (the oldest match).
   */
  private reconcilePending(rawText: string, queueMessageId?: string): void {
    // ID PATH (preferred). The orchestrator stamps the queue id it dispatched
    // under onto the echo, and every queue-backed pending row carries that same
    // `msgId`, so this is an exact match with nothing to normalise.
    //
    // The text path below cannot be relied on: pencil-api appends blocks to the
    // outgoing message at dispatch — the attachment block, and the locale
    // directive on EVERY turn — so the echoed text is not the text the user
    // typed (measured: 61 chars sent, 1042 echoed). Equality therefore never
    // held, the row was never retired, and because the feed renders
    // `[...history, ...pending]` it stranded below the agent's own answer.
    if (queueMessageId) {
      const list = this.pendingUserTurnsSubject.value;
      const idx = list.findIndex((t) => t.msgId === queueMessageId);
      // Record the id as echoed even when no row matches: applyQueueState uses
      // it to keep the server from re-adding a row history already draws, and
      // the queue snapshot can easily arrive AFTER this echo.
      this.echoedQueueMsgIds.add(queueMessageId);
      if (idx >= 0) {
        const next = list.slice();
        next.splice(idx, 1);
        this.zone.run(() => this.pendingUserTurnsSubject.next(next));
      }
      return;
    }
    return this.reconcilePendingByText(rawText);
  }

  /** Legacy content match. Still reached for turns that never went through the
   *  platform queue (voice, inbound email, system) and for orchestrators older
   *  than the `queueMessageId` echo field. Kept as-is deliberately: it is the
   *  only handoff those paths have. */
  private reconcilePendingByText(rawText: string): void {
    // With the platform queue authoritative, the running message is still shown
    // (see applyQueueState) precisely so it doesn't vanish for the seconds
    // between dispatch and the agent's first output. THIS echo is the handoff:
    // the bubble now exists in `history`, so the row must go or it draws twice.
    //
    // Only RUNNING rows are eligible. Content-matching the whole list would let
    // one user's echo delete another user's identically-worded QUEUED message —
    // the queue serialises turns, so there is at most one running row and the
    // ambiguity disappears.
    // The echo carries the hosted-attachment block appended at dispatch; the
    // queued row does not. Comparing raw would never match for a message with
    // files, leaving the row in place and drawing the message twice.
    const text = splitAttachments(rawText).body;
    if (this.queueAuthoritative) {
      const echoed = text.trim();
      this.echoedRunningTexts.add(echoed);
      const list = this.pendingUserTurnsSubject.value;
      const idx = list.findIndex(
        (t) => t.status === 'active' && t.fromServer && t.text.trim() === echoed,
      );
      if (idx >= 0) {
        const next = list.slice();
        next.splice(idx, 1);
        this.zone.run(() => this.pendingUserTurnsSubject.next(next));
      }
      return;
    }
    const trimmed = text.trim();
    const list = this.pendingUserTurnsSubject.value;
    const idx = list.findIndex((t) => t.status !== 'failed' && t.text === trimmed);
    if (idx < 0) {
      return;
    }
    const next = list.slice();
    next.splice(idx, 1);
    this.zone.run(() => this.pendingUserTurnsSubject.next(next));
  }

  /** A turn just started → promote the oldest queued optimistic turn to active. */
  private promoteOldestQueued(): void {
    // Same reason as reconcilePending: with the server queue live, promotion is
    // driven by `ai_queue_dispatched`, and guessing here would promote the wrong
    // row whenever more than one person has something queued.
    if (this.queueAuthoritative) {
      return;
    }
    const list = this.pendingUserTurnsSubject.value;
    const idx = list.findIndex((t) => t.status === 'queued');
    if (idx < 0) {
      return;
    }
    const next = list.slice();
    next[idx] = { ...next[idx], status: 'active' };
    this.zone.run(() => this.pendingUserTurnsSubject.next(next));
  }

  // ── Platform queue (PLATFORM_QUEUE_SPEC.md) ────────────────────────────────

  /**
   * Fetch the authoritative queue snapshot.
   *
   * Galactus has no replay buffer and `realtimePublish` swallows its own errors,
   * so a client that was mid-reconnect during a transition missed it PERMANENTLY.
   * This is the recovery path, and it is why the queue is never event-sourced on
   * the client. Called on connect, on every SSE reconnect, and on every Galactus
   * (re)connection — those are two independent transports and either one can miss
   * a transition while the other looks perfectly healthy.
   *
   * A failure here is non-fatal: we simply stay on the legacy local-optimism path
   * rather than blanking the panel.
   */
  /**
   * THE DEFAULT since the cutover: history comes from the STORED transcript.
   *
   * `?transcriptSource=sse` opts a single tab back onto the old SSE replay. The
   * escape hatch is kept deliberately, and it is the same one that made this
   * testable: support can open a complaining user's board both ways, on the same
   * board with the same auth, and see which source is wrong.
   *
   * WHAT THIS DOES NOT DO, on purpose. The SSE stream still replays the whole
   * session on connect and is still filtered by `dbWatermark`; `seenEventKeys`
   * still dedupes; `REPLAY_BUFFER_MAX` is still sized to reconstruct a session.
   * §13.7 eventually narrows the subscription to the live tail and deletes those
   * — but doing it in the same change would make rollback a DEPLOY instead of a
   * one-line revert, and it would do it at exactly the moment rollback matters
   * most. They come out separately, after this has run.
   *
   * It changes NOTHING about writing: the queue, utter, and stop paths are
   * untouched, and it cannot widen access — /ai-session/:id/messages is
   * member-gated exactly like every sibling route.
   */
  private get dbTranscriptEnabled(): boolean {
    if (this.dbTranscriptFlag === undefined) {
      try {
        this.dbTranscriptFlag =
          new URLSearchParams(window.location.search).get('transcriptSource') !==
          'sse';
      } catch {
        // A tab with no parsable location still gets the default. Failing CLOSED
        // here would silently put someone back on the retired path and make a
        // storage bug invisible again.
        this.dbTranscriptFlag = true;
      }
    }
    return this.dbTranscriptFlag;
  }
  private dbTranscriptFlag: boolean | undefined;

  /**
   * How far the STORED history reaches, as `(epoch, seq)`.
   *
   * The seam this exists for: with the flag on, history comes from the DB but
   * the SSE stream still replays the whole session on connect, so without a
   * watermark every stored turn would render twice. Compared on `(epoch, seq)`
   * and not on text — matching transcript text is what made `reconcilePending`
   * misfire once the locale directive started riding along with user messages.
   */
  private dbWatermark: { epoch: number; seq: number } | null = null;

  /**
   * Cursor for the NEXT older page (`"<epoch>:<seq>"`), or null when the
   * transcript is exhausted. Opaque on purpose — a bare `seq` has no generation
   * and would page into the wrong one.
   */
  private dbOlderCursor: string | null = null;
  private dbLoadingOlder = false;

  /** True while an older page is in flight, so the panel can show a spinner. */
  private readonly loadingOlderSubject = new BehaviorSubject<boolean>(false);
  readonly loadingOlder$: Observable<boolean> =
    this.loadingOlderSubject.asObservable();

  /** Whether there is more stored history above what is rendered. */
  get hasOlderTranscript(): boolean {
    return this.dbTranscriptEnabled && this.dbOlderCursor !== null;
  }

  /**
   * Fetch the next older page and PREPEND it.
   *
   * Prepending rather than appending is the whole point: these turns are older
   * than everything on screen, and the feed reads oldest-first. The caller is
   * responsible for holding scroll position — see the panel's
   * `loadOlderTranscript`, which pins scrollHeight across the splice so the
   * viewport does not jump to the top the moment new content lands above it.
   *
   * Never touches `dbWatermark`: that marks where the FIRST page reached, which
   * is what decides whether a replayed live event is redundant. Older pages are
   * strictly below it and change nothing about the live seam.
   */
  async loadOlderTranscript(): Promise<number> {
    const spaceId = this.currentSpaceId;
    if (
      !this.dbTranscriptEnabled ||
      !spaceId ||
      !this.dbOlderCursor ||
      this.dbLoadingOlder
    ) {
      return 0;
    }
    this.dbLoadingOlder = true;
    this.loadingOlderSubject.next(true);
    try {
      const url =
        `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/messages` +
        `?steps=1&limit=50&before=${encodeURIComponent(this.dbOlderCursor)}`;
      const page = await firstValueFrom(
        this.http.get<StoredTranscriptPage>(url).pipe(catchError(() => of(null))),
      );
      // A space switch may have landed while this was in flight; dropping the
      // result is correct — the cursor belongs to the space we left.
      if (!page || this.currentSpaceId !== spaceId) {
        return 0;
      }
      const older: AgentStreamEvent[] = [];
      for (const turn of [...(page.turns ?? [])].reverse()) {
        for (const row of turn.steps ?? []) {
          const ev = this.storedRowToEvent(row);
          if (ev) {
            older.push(ev);
          }
        }
      }
      for (const ev of older) {
        const key = this.eventKey(ev);
        if (key !== null) {
          this.seenEventKeys.add(key);
        }
      }
      this.dbOlderCursor = page.cursor?.hasMore
        ? (page.cursor.before ?? null)
        : null;
      if (older.length) {
        this.historySubject.next(older.concat(this.historySubject.value));
      }
      return older.length;
    } finally {
      this.dbLoadingOlder = false;
      this.loadingOlderSubject.next(false);
    }
  }

  /** True if this event is already covered by the stored history we loaded. */
  private coveredByStoredHistory(ev: AgentStreamEvent): boolean {
    if (!this.dbTranscriptEnabled || !this.dbWatermark) {
      return false;
    }
    const e = ev as AgentStreamEvent & { epoch?: number; seq?: number };
    if (typeof e.epoch !== 'number' || typeof e.seq !== 'number') {
      // An event without ordering metadata cannot be placed against the
      // watermark. Render it: a duplicate is recoverable, a silently dropped
      // reply is not.
      return false;
    }
    return (
      e.epoch < this.dbWatermark.epoch ||
      (e.epoch === this.dbWatermark.epoch && e.seq <= this.dbWatermark.seq)
    );
  }

  /**
   * Load the stored transcript and render it as a flat feed.
   *
   * `steps=1` returns each turn's rows inline, so a page costs ONE request
   * rather than one per turn. The rows are mapped back into the same
   * `AgentStreamEvent` shape the SSE path produces — including re-adding the
   * `[user] ` prefix that marks a user turn — so the existing renderer draws
   * this unchanged. That is deliberate: this flag is meant to test whether the
   * STORED DATA is right, and swapping the rendering at the same time would
   * make a bad result impossible to attribute.
   *
   * Non-fatal by design. If it fails we leave history empty and let the SSE
   * replay populate it exactly as today, rather than blanking the panel.
   */
  private async loadStoredHistory(spaceId: string): Promise<void> {
    const url =
      `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/messages` +
      '?steps=1&limit=100';
    const page = await firstValueFrom(
      this.http.get<StoredTranscriptPage>(url).pipe(catchError(() => of(null))),
    );
    // A space switch may have landed while this was in flight.
    if (!page || this.currentSpaceId !== spaceId) {
      return;
    }

    // The API pages newest-first; the feed reads oldest-first.
    const events: AgentStreamEvent[] = [];
    for (const turn of [...(page.turns ?? [])].reverse()) {
      for (const row of turn.steps ?? []) {
        const ev = this.storedRowToEvent(row);
        if (ev) {
          events.push(ev);
        }
      }
    }

    this.historySubject.next(events);
    // Seed the dedupe set so the SSE replay of these same events is dropped by
    // the existing guard as well as by the watermark — belt and braces, because
    // an event missing `seq` slips past the watermark by design.
    for (const ev of events) {
      const key = this.eventKey(ev);
      if (key !== null) {
        this.seenEventKeys.add(key);
      }
    }
    if (typeof page.epoch === 'number' && typeof page.latestSeq === 'number') {
      this.dbWatermark = { epoch: page.epoch, seq: page.latestSeq };
    }
    // Where the next (older) page starts. `hasMore` is the authority, not a
    // non-null cursor: the last page still returns the cursor of its own oldest
    // turn, and paging on that would re-fetch the same turns forever.
    this.dbOlderCursor = page.cursor?.hasMore
      ? (page.cursor.before ?? null)
      : null;
  }

  /** One stored row → the stream event shape the panel already renders. */
  private storedRowToEvent(row: StoredTranscriptRow): AgentStreamEvent | null {
    const ts = row.ts ? new Date(row.ts).getTime() : 0;
    const base = {
      ts,
      seq: row.seq,
      epoch: row.epoch,
      sessionKey: row.sessionKey,
      isSubagent: row.isSubagent,
    };
    switch (row.kind) {
      case 'text':
        return {
          ...base,
          kind: 'text',
          // Re-add the marker the orchestrator echoes, so the existing
          // "is this a user turn?" check keeps working untouched.
          text: row.role === 'user' ? `[user] ${row.text ?? ''}` : (row.text ?? ''),
          ...(row.queueMessageId ? { queueMessageId: row.queueMessageId } : {}),
          // The author, under the names the live echo uses, so a reloaded panel
          // attributes a message exactly as the live one did (name + photo, not
          // a blank avatar).
          ...(row.role === 'user' && row.authorName ? { user: row.authorName } : {}),
          ...(row.role === 'user' && row.authorUid
            ? { userUid: String(row.authorUid) }
            : {}),
          ...(row.role === 'user' && row.via ? { via: row.via } : {}),
        } as AgentStreamEvent;
      case 'gap':
        // An imported turn whose reply the session reset destroyed. Rendered as
        // an explicit statement, NEVER as a turn where the agent did nothing —
        // with the SSE replay gone there is no other source telling the truth.
        return {
          ...base,
          kind: 'text',
          text: row.text ?? "The agent's reply to this message is no longer stored.",
          isGap: true,
        } as unknown as AgentStreamEvent;
      case 'tool_call':
        return {
          ...base,
          kind: 'tool_call',
          toolName: row.toolName ?? '',
          toolUseId: row.toolUseId ?? '',
          argsPreview: row.argsPreview ?? '',
        } as AgentStreamEvent;
      case 'tool_result':
        return {
          ...base,
          kind: 'tool_result',
          toolName: row.toolName ?? '',
          toolUseId: row.toolUseId ?? '',
          resultPreview: row.resultPreview ?? '',
          isError: !!row.isError,
        } as AgentStreamEvent;
      case 'turn_started':
      case 'turn_completed':
        return {
          ...base,
          kind: row.kind,
          ...(row.stopReason ? { stopReason: row.stopReason } : {}),
        } as AgentStreamEvent;
      default:
        // speak/plan and anything newer: not part of the flat transcript.
        return null;
    }
  }

  private async fetchQueueSnapshot(): Promise<void> {
    const spaceId = this.currentSpaceId;
    if (!spaceId) {
      return;
    }
    const url = `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/queue`;
    const state = await firstValueFrom(
      this.http.get<QueueWireState>(url).pipe(catchError(() => of(null))),
    );
    // Guard against a space switch that landed while this was in flight.
    if (!state || this.currentSpaceId !== spaceId) {
      return;
    }
    this.applyQueueState(state);
  }

  /**
   * Apply a queue state from the snapshot GET or from any Galactus queue event.
   *
   * Every queue event carries the WHOLE state rather than a delta, so this is
   * idempotent and self-healing: an out-of-order or duplicated event converges to
   * the same list instead of drifting.
   *
   * Rendering rule: show only messages that have NOT started running. A message
   * the agent has picked up is echoed by the orchestrator onto the SSE stream and
   * renders from `history`; keeping it here too would draw the same bubble twice.
   * The only locally-owned rows we keep are our own not-yet-acknowledged sends.
   */
  applyQueueState(state: QueueWireState): void {
    if (!state || !Array.isArray(state.messages)) {
      return;
    }
    // A late event for a space we've left must not repopulate this panel.
    if (this.currentSpaceId && state.spaceId && state.spaceId !== this.currentSpaceId) {
      return;
    }
    this.queueAuthoritative = true;
    this.serverQueue = state;
    this.pendingSpaceId = state.spaceId || this.currentSpaceId;

    for (const m of state.messages) {
      if (m.msgId && m.via) {
        this.viaByMsgId.set(m.msgId, m.via);
      }
    }
    const myUid = this.currentUserId();
    const iAmHost = Session.isOwnedByUser(
      this.spaceRepo.activeSpace,
      this.userService.user?.value?.user,
    );

    const serverRows: PendingUserTurn[] = state.messages
      .filter((m) => {
        if (m.status === 'queued') {
          return true;
        }
        // A message the agent has PICKED UP but not yet echoed. Keeping it is
        // the whole point: on an idle space the server dispatches immediately,
        // so the message is never `queued`, and dropping it here left the panel
        // empty for the 5-8s until the agent's first output arrived — the send
        // appeared to vanish. Show it until `history` has it (reconcilePending
        // records the echo), then let history own the bubble.
        if (m.status === 'dispatching' || m.status === 'active') {
          // Prefer the id: `echoedRunningTexts` is keyed by the ECHOED text,
          // which pencil-api has appended to by the time it comes back, so it
          // never matched `displayText` and this row kept being re-added on
          // every snapshot until the message left the queue. The id has no such
          // problem. Text stays as the fallback for non-queue paths.
          if (m.msgId && this.echoedQueueMsgIds.has(m.msgId)) {
            return false;
          }
          return !this.echoedRunningTexts.has(
            (m.displayText || m.text || '').trim(),
          );
        }
        return false;
      })
      .map((m) => ({
        clientId: m.clientId || m.msgId,
        msgId: m.msgId,
        text: m.displayText || m.text,
        ts: m.createdAt ? Date.parse(m.createdAt) : Date.now(),
        ...(m.authorName ? { author: m.authorName } : {}),
        ...(m.authorUid ? { authorUid: m.authorUid } : {}),
        ...(m.via ? { via: m.via } : {}),
        status: m.status === 'queued' ? ('queued' as const) : ('active' as const),
        fromServer: true,
        // Author, or host. An unattributed message (voice, email, an older row
        // queued before authorUid existed) is nobody's to edit but the host's —
        // guessing "yours" would hand a stranger's words to whoever looked.
        // A RUNNING message is nobody's to edit or cancel — it has already been
        // handed to the agent, so the queue's edit/cancel routes 409 it anyway;
        // offering the control would only produce a button that always fails.
        canMutate:
          m.status === 'queued' && (iAmHost || (!!myUid && m.authorUid === myUid)),
      }));

    // Keep our own in-flight sends — the POST hasn't returned yet, so the server
    // genuinely doesn't know about them. Anything the server HAS acknowledged is
    // dropped here and re-added from `serverRows`, so it can never appear twice.
    const serverClientIds = new Set(state.messages.map((m) => m.clientId).filter(Boolean));
    const localRows = this.pendingUserTurnsSubject.value.filter(
      (t) => !t.fromServer && t.status === 'sending' && !serverClientIds.has(t.clientId),
    );

    this.zone.run(() => this.pendingUserTurnsSubject.next([...localRows, ...serverRows]));
  }

  /** Synchronous read of the server's queue state, for the panel's status chip. */
  get queueState(): QueueWireState | null {
    return this.serverQueue;
  }

  /** The signed-in user's Mongo id, which is also their Firebase uid. */
  private currentUserId(): string {
    return String(this.userService.user?.value?.user?._id || '');
  }

  /**
   * Cancel a message that hasn't started running (PLATFORM_QUEUE_SPEC.md §12b).
   *
   * The response carries the resulting queue state, so we apply it directly
   * rather than waiting for the Galactus echo — the author who clicked should
   * see the row disappear on their own round trip even if the broadcast is lost.
   *
   * A 409 means the message started running, or isn't theirs to cancel; the two
   * are deliberately indistinguishable. Either way the recovery is the same and
   * it's already happened: `state` comes back on the error body too, so the row
   * re-renders as whatever it actually is now.
   */
  async cancelQueued(msgId: string): Promise<boolean> {
    return this.mutateQueued(msgId, null);
  }

  /** Replace the text of a message that hasn't started running. Same 409
   *  semantics as {@link cancelQueued}. */
  async editQueued(msgId: string, text: string): Promise<boolean> {
    const trimmed = text.trim();
    if (!trimmed) {
      return false;
    }
    return this.mutateQueued(msgId, trimmed);
  }

  /** Shared body of cancel (`text === null`) and edit. */
  private async mutateQueued(msgId: string, text: string | null): Promise<boolean> {
    const spaceId = this.spaceRepo.activeSpaceId;
    if (!spaceId || !msgId) {
      return false;
    }
    const url = `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/queue/${msgId}`;
    const request$ =
      text === null
        ? this.http.delete<QueueMutationReply>(url)
        : this.http.patch<QueueMutationReply>(url, { text });

    const reply = await firstValueFrom(
      request$.pipe(
        // A 409 is an ANSWER, not a failure: it carries the true queue state.
        // Unwrap it so the panel re-renders from the server rather than sitting
        // on a row the server has already moved on from.
        catchError((err: { status?: number; error?: QueueMutationReply }) =>
          of(err?.status === 409 && err.error ? err.error : null),
        ),
      ),
    );
    if (!reply) {
      return false;
    }
    // No space-switch guard here on purpose — applyQueueState already drops a
    // payload whose spaceId isn't the one we're on, and duplicating the check
    // would be stricter, not safer: it would also drop a legitimate reply that
    // landed before connect() had recorded the space.
    if (reply.state) {
      this.applyQueueState(reply.state);
    }
    return !!reply.ok;
  }

  private setConnectionState(s: ConnectionState): void {
    if (this.connectionStateSubject.value === s) {
      return;
    }
    this.zone.run(() => this.connectionStateSubject.next(s));
  }

  /** The board the user is currently viewing, in the wire shape, or null. */
  private activeFramePayload(
    board?: Frame,
  ): { roomId: string; frameUid: string; frameName?: string } | null {
    const b = board ?? this.spaceBoardsService.activeSpaceSelectedBoard;
    // A Frame doesn't carry its room; the board the user is viewing lives in the
    // current room, so the roomId is the active room's uid.
    const roomId = this.spaceRepo.activeSpaceCurrentRoomUid;
    if (!b?.uid || !roomId) {
      return null;
    }
    return { roomId, frameUid: b.uid, frameName: b.name };
  }

  /**
   * Push the active board to the orchestrator out-of-band (the voice path can't
   * carry it with the utterance — the LiveKit worker forwards only text). The
   * orchestrator stashes it per-space and defaults the whiteboard tools to it.
   * Fire-and-forget, best-effort.
   */
  private pushActiveFrame(board?: Frame): void {
    const spaceId = this.spaceRepo.activeSpaceId;
    const payload = this.activeFramePayload(board);
    if (!spaceId || !payload) {
      return;
    }
    const url = `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/active-frame`;
    this.http
      .post(url, payload)
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  /**
   * Open the SSE stream. If we're already connected to the same space,
   * no-op. On disconnect (error or completion) we auto-reconnect with
   * backoff up to ~30s.
   */
  private async connect(): Promise<void> {
    const spaceId = this.spaceRepo.activeSpaceId;
    if (!spaceId) {
      return;
    }
    if (this.abortController && this.currentSpaceId === spaceId) {
      return;
    }
    this.disconnect();
    this.currentSpaceId = spaceId;
    this.reconnectAttempt = 0;
    this.historySubject.next([]);
    // Paired with history: these only exist to say "history already draws this".
    this.echoedRunningTexts.clear();
    this.echoedQueueMsgIds.clear();
    // Same lifetime as history: a fresh connect re-receives the whole replay and
    // must render it, so stale keys from the previous session would blank it.
    this.seenEventKeys.clear();
    // Paired with history for the same reason — a watermark outliving the
    // history it describes would suppress the replay that should redraw it.
    this.dbWatermark = null;
    // Same lifetime: a cursor from the previous space would page THAT space's
    // transcript into this one.
    this.dbOlderCursor = null;
    this.planSubject.next({ items: [] });
    // Optimistic pending turns are PER-SPACE. They survive RECONNECTS (which go
    // through runStream, not connect) so an in-flight turn isn't lost on a blip —
    // but they must NOT leak into a DIFFERENT space's panel. We key them on the
    // space they were sent in (`pendingSpaceId`), tracked independently of the
    // connection lifecycle: `disconnect()` nulls `currentSpaceId`, so a
    // close-panel → switch-space → reopen sequence would otherwise fail to detect
    // the change. Without this, an unreconciled optimistic turn from the previous
    // space — one sent, then abandoned before the orchestrator echoed it back —
    // renders as a stray user bubble in the new space (history/plan are cleared
    // above; pending was the one buffer that wasn't).
    if (this.pendingSpaceId !== undefined && this.pendingSpaceId !== spaceId) {
      this.pendingUserTurnsSubject.next([]);
      this.pendingSpaceId = undefined;
      this.turnActive = false; // stale busy-state from the old space must not carry over
      // The queue is PER-SPACE too: another space's state must not decide whether
      // this space's panel trusts the server or falls back to local inference.
      this.serverQueue = null;
      this.queueAuthoritative = false;
    }
    // Behind the flag, seed history from storage BEFORE the stream opens, so
    // the watermark is in place when the replay lands and the panel does not
    // briefly render the same turns from both sources.
    if (this.dbTranscriptEnabled) {
      void this.loadStoredHistory(spaceId).then(() => this.runStream());
    } else {
      this.runStream();
    }
    // The snapshot is the authority; Galactus only supplies deltas on top of it.
    void this.fetchQueueSnapshot();
  }

  private async runStream(): Promise<void> {
    if (!this.currentSpaceId) {
      return;
    }
    const spaceId = this.currentSpaceId;
    this.setConnectionState(this.reconnectAttempt === 0 ? 'connecting' : 'reconnecting');
    const abort = new AbortController();
    this.abortController = abort;

    let token: string | null = null;
    try {
      token = await firstValueFrom(this.authService.getToken());
    } catch (e) {
      console.warn('[ai-session-stream] failed to get token', e);
    }

    const url = `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/stream`;
    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: abort.signal,
        // No cache; SSE streams must always go to the network.
        cache: 'no-store',
      });
      if (!res.ok || !res.body) {
        console.warn('[ai-session-stream] HTTP', res.status, res.statusText);
        this.scheduleReconnect();
        return;
      }
      // NOTE: do NOT clear history here on a reconnect. The orchestrator's stream
      // has a "poller-grace" that SUPPRESSES the full re-broadcast on a quick
      // reconnect (to stop the worker re-narrating) — so clearing would WIPE the
      // feed and the replay would never refill it (that's the "response vanished"
      // bug). History is cleared only on a FRESH connect / space switch (connect()).
      // A reconnect just appends, and handleSseFrame dedups by event key so the
      // replay it DOES receive is a no-op.
      //
      // CORRECTION (measured over CDP 2026-08-11): the two claims that used to
      // sit here were both false. Poller-grace does NOT suppress this replay —
      // it suppresses a restarting poller re-publishing to ALL subscribers (the
      // narration flood). `EventBus.subscribe()` backfills the whole buffer to
      // every new subscriber unconditionally, and pencil-api opens a fresh
      // upstream per browser connection, so a reconnecting panel receives the
      // entire session again. And feed events DO carry a usable identity: every
      // one has a millisecond `ts` plus a per-kind discriminator. Believing
      // otherwise cost 4.2x duplicated rows after 25 minutes, unbounded.
      // Mark 'live' once the stream has STAYED OPEN past a short grace, even with
      // no frames — a fresh/idle session has nothing to replay (the bootstrap is
      // deliver:false), so no frame arrives, but the stream IS connected. First-
      // frame also marks live immediately (handleSseFrame). Cleared below if the
      // stream ends/aborts before the grace (the not-ready close → we reconnect and
      // stay connecting/reconnecting instead of falsely flashing live).
      const liveTimer = setTimeout(() => this.setConnectionState('live'), 1200);
      try {
        await this.parseSseStream(res.body);
      } finally {
        clearTimeout(liveTimer);
      }
      // parseSseStream returned = the SERVER closed the stream cleanly while the
      // panel is still open. The common cause is the very bug this fixes: on the
      // first Start-AI click the orchestrator isn't ready yet, so pencil-api opens
      // the SSE (200), emits a one-shot event, and closes — leaving the panel empty
      // forever because we never retried. Reconnect (with backoff). A user-initiated
      // panel close aborts the fetch → AbortError below → currentSpaceId cleared →
      // scheduleReconnect() no-ops, so this only retries genuine drops.
      this.scheduleReconnect();
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        return;
      } // user closed the panel
      console.warn('[ai-session-stream] stream error', err);
      this.scheduleReconnect();
    }
  }

  /**
   * Parse an SSE-formatted byte stream: lines are `data: <payload>` separated
   * by blank lines. Comments (`: …`) and named events are ignored for now;
   * we only care about default-event `data:` frames.
   */
  private async parseSseStream(body: ReadableStream<Uint8Array>): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let done = false;
    while (!done) {
      const result = await reader.read();
      done = result.done;
      if (done) {
        break;
      }
      buffer += decoder.decode(result.value, { stream: true });
      // SSE frames are separated by \n\n.
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) >= 0) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        this.handleSseFrame(frame);
      }
    }
  }

  private handleSseFrame(frame: string): void {
    // A frame can be multiple lines (data: chunked across lines), but our
    // server emits one line per frame. Still, handle the general case.
    const dataLines: string[] = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith(':')) {
        continue;
      } // SSE comment
      if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    }
    if (dataLines.length === 0) {
      return;
    }
    const payload = dataLines.join('\n');
    try {
      const ev = JSON.parse(payload) as AgentStreamEvent;
      // A real frame arrived → the stream is healthy → reset the reconnect backoff.
      // (Doing this here, rather than on a bare 200, means a stream that opens and
      // immediately closes with no data still escalates its backoff.)
      this.reconnectAttempt = 0;
      this.setConnectionState('live'); // a frame arrived → the stream is live
      this.zone.run(() => {
        // Phase-1 queue inference + optimistic-turn reconcile
        // (INTERACTIVE_QUEUE_SPEC.md). turn_started/completed drive the "is the
        // agent busy" flag (→ queued vs active); a `text` event WITH `user` set is
        // the orchestrator echoing a user turn → drop the matching optimistic one.
        if (ev.kind === 'turn_started') {
          this.turnActive = true;
          this.promoteOldestQueued();
        } else if (ev.kind === 'turn_completed') {
          this.turnActive = false;
        } else if (ev.kind === 'text' && ev.text.startsWith('[user] ')) {
          // The orchestrator echoes a user turn with a `[user] ` prefix (the panel
          // strips it). Retire the matching optimistic turn — by queue id when the
          // orchestrator stamped one, else by text.
          this.reconcilePending(ev.text.slice('[user] '.length), ev.queueMessageId);
        }
        // The pinned checklist is a derived state, not part of the scrolling
        // feed — fold plan events into it and keep them out of the history list.
        if (ev.kind === 'plan') {
          this.applyPlanEvent(ev);
          this.eventsSubject.next(ev);
          return;
        }
        // Drop an event we've already rendered. EVERY reconnect re-subscribes to
        // the orchestrator bus, and `EventBus.subscribe()` backfills the whole
        // replay buffer to each new subscriber before attaching it for live
        // events — so on a reconnect the entire session arrives a second time.
        // Since we deliberately do NOT clear history there (see runStream), the
        // feed used to grow by a full copy of itself on every reconnect.
        //
        // MEASURED 2026-08-11 over CDP, 25-minute run: the stream is aborted on
        // a ~300s clock and reopens ~1s later, and the feed went
        // 34→72→148→244→360→496 rows against 118 real ones — 4.2x and unbounded.
        // A long-lived space therefore accumulates thousands of duplicate rows.
        //
        // Deduping rather than clearing is deliberate: clearing on reconnect
        // would reintroduce the "response vanished" bug the moment a replay
        // arrives empty (the poller clears its buffer on teardown), whereas an
        // already-seen event is safe to drop under every ordering.
        // Behind `?transcriptSource=db`, history already came from storage, so
        // the replay of everything at or below the watermark is redundant.
        // Dropped on `(epoch, seq)` rather than on text — see `dbWatermark`.
        if (this.coveredByStoredHistory(ev)) {
          return;
        }
        const key = this.eventKey(ev);
        if (key !== null && this.seenEventKeys.has(key)) {
          return;
        }
        if (key !== null) {
          this.seenEventKeys.add(key);
        }
        const next = this.historySubject.value.concat(ev);
        this.historySubject.next(next);
        this.eventsSubject.next(ev);
      });
    } catch (e) {
      console.warn('[ai-session-stream] parse error', e, payload.slice(0, 200));
    }
  }

  private scheduleReconnect(): void {
    if (!this.currentSpaceId) {
      return;
    }
    this.setConnectionState('reconnecting');
    this.reconnectAttempt += 1;
    // 1s, 2s, 4s, 8s, capped at 16s. (Matches the auto-reconnect cadence
    // EventSource gives natively.)
    const delayMs = Math.min(16_000, 1000 * 2 ** (this.reconnectAttempt - 1));
    setTimeout(() => {
      if (!this.currentSpaceId) {
        return;
      }
      this.runStream();
      // We were disconnected, so we missed every Galactus queue event in the
      // gap — and they are not replayed. Re-fetch the authority rather than
      // carrying a queue state that silently stopped being true.
      void this.fetchQueueSnapshot();
    }, delayMs);
  }

  private disconnect(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
    this.currentSpaceId = undefined;
    this.setConnectionState('idle');
  }
}
